import React, { useRef, useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Accordion,
  Card,
  ListGroup,
  ListGroupItem,
} from "react-bootstrap";

const VideoPlayer = ({
  videoUrl,
  timeStampCategories,
  useLandscape,
  onVideoEnd,
  onVideoTimeUpdate,
  onCategoryClick,
  playbackRate = 1.0,
  hideChapters = false,
}) => {
  const videoRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [activeIndex, setActiveIndex] = useState({
    categoryIndex: null,
    detailIndex: null,
  });
  const [accordionActiveKey, setAccordionActiveKey] = useState(null);
  const [userSelectedCategoryIndex, setUserSelectedCategoryIndex] =
    useState(null);

  const [isEndSlide, setIsEndSlide] = useState(false);
  // Browsers block autoplay with sound when the viewer lands on the page
  // without interacting first (e.g. opening a demo link straight from an
  // email), so fall back to an obvious play button.
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);

  const formatText = (text) => {
    var output = text;

    if (!text) return output;

    // replace any html or script tags
    output = output.replace(/<.*?>/g, "");

    // Add line breaks and bold text
    output = output
      .replace(/\n/g, "<br/>")
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

    return output;
  };

  const jumpTo = (time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      videoRef.current.play();
    }
  };

  const handleAccordionToggle = (categoryIndex) => {
    const accordionActiveKeyAsInt = parseInt(accordionActiveKey);
    const newCategoryIndex =
      accordionActiveKeyAsInt === categoryIndex ? null : categoryIndex;

    setAccordionActiveKey(`${newCategoryIndex}`);
    setUserSelectedCategoryIndex(newCategoryIndex);

    if (onCategoryClick) onCategoryClick(categoryIndex);

    // Jump to the first detail of the selected category
    if (newCategoryIndex !== null) {
      jumpTo(
        timeStampCategories[newCategoryIndex].timeStampDetails[0].startTime
      );
    }

    // Reset setUserSelectedCategoryIndex after 5 seconds
    setTimeout(() => {
      setUserSelectedCategoryIndex(null);
    }, 5000);
  };

  const playVideo = () => {
    if (!videoRef.current) return;
    const attempt = videoRef.current.play();
    // If playback still fails, drop the overlay so the native controls
    // underneath are reachable rather than leaving a dead button.
    if (attempt) attempt.catch(() => setAutoplayBlocked(false));
  };

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;
    let cancelled = false;

    setAutoplayBlocked(false);
    const handlePlay = () => setAutoplayBlocked(false);
    videoElement.addEventListener("play", handlePlay);

    // Changing the <source> alone doesn't swap the media, so reload first
    videoElement.load();
    const attempt = videoElement.play();
    if (attempt) {
      attempt.catch((err) => {
        if (!cancelled && err.name === "NotAllowedError") {
          setAutoplayBlocked(true);
        }
      });
    }

    return () => {
      cancelled = true;
      videoElement.removeEventListener("play", handlePlay);
    };
  }, [videoUrl]);

  useEffect(() => {
    const handleTimeUpdate = () => {
      if (videoRef.current) {
        const current = videoRef.current.currentTime;
        const currentTime = Math.round(current);
        setCurrentTime(currentTime);

        // Determine which TimeStampDetail is currently active
        let foundActive = false;
        timeStampCategories.forEach((category, categoryIndex) => {
          category.timeStampDetails.forEach((detail, detailIndex) => {
            if (
              current >= detail.startTime &&
              current <= detail.endTime &&
              !foundActive
            ) {
              foundActive = true;
              if (
                categoryIndex !== activeIndex.categoryIndex ||
                detailIndex !== activeIndex.detailIndex
              )
                setActiveIndex({
                  categoryIndex,
                  detailIndex,
                });
            }
          });
        });
        if (!foundActive) {
          setActiveIndex({ categoryIndex: null, detailIndex: null });
        }
      }
    };

    const videoElement = videoRef.current;
    if (videoElement) {
      videoElement.addEventListener("timeupdate", handleTimeUpdate);
      videoElement.playbackRate = playbackRate;
      if (onVideoTimeUpdate)
        videoElement.addEventListener("timeupdate", onVideoTimeUpdate);
      if (onVideoEnd) videoElement.addEventListener("ended", onVideoEnd);
    }

    return () => {
      if (videoElement) {
        videoElement.removeEventListener("timeupdate", handleTimeUpdate);
        if (onVideoTimeUpdate)
          videoElement.removeEventListener("timeupdate", onVideoTimeUpdate);
        if (onVideoEnd) videoElement.removeEventListener("ended", onVideoEnd);
      }
    };
  }, [timeStampCategories]);

  useEffect(() => {
    if (activeIndex.categoryIndex !== null) {
      setAccordionActiveKey(
        null !== userSelectedCategoryIndex
          ? `${userSelectedCategoryIndex}`
          : `${activeIndex.categoryIndex}`
      );

      // Set if the end slide is active
      if (activeIndex.categoryIndex === timeStampCategories.length - 1) {
        if (
          activeIndex.detailIndex ===
          timeStampCategories[activeIndex.categoryIndex].timeStampDetails
            .length -
          1
        ) {
          setIsEndSlide(true);
        } else {
          setIsEndSlide(false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  return (
    <Container fluid className="video-player-container">
      <Row>
        {/* Hidden on small sizes and landscape */}
        {!useLandscape && (
          <Col xl={4} className={"d-none d-xl-block"}>
            <div
              style={{
                height: "100%",
                backgroundColor: "var(--blue)",
                borderRadius: "10px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "19px",
                textAlign: isEndSlide ? "left" : "center",
                color: "white",
                fontSize: "30px",
              }}
            >
              {activeIndex &&
                activeIndex.categoryIndex !== null &&
                activeIndex.detailIndex !== null ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: formatText(
                      timeStampCategories[activeIndex.categoryIndex]
                        .timeStampDetails[activeIndex.detailIndex].slideText
                    ),
                  }}
                ></div>
              ) : null}
            </div>
          </Col>
        )}
        <Col
          xs={12}
          lg={hideChapters ? 12 : 6}
          xl={hideChapters ? 12 : useLandscape ? 8 : 4}
          className="video-container"
          style={{
            borderRadius: "10px",
            display: "flex",
            justifyContent: "center",
            marginTop: "-10px", // Offset the video white space
          }}
        >
          <div
            className="video-player-frame"
            style={{
              // Without a chapter list the video would stretch the full
              // container width, so cap it and let the flex parent center it.
              maxWidth: hideChapters ? "800px" : undefined,
            }}
          >
            <video
              ref={videoRef}
              width="100%"
              height="auto"
              controls
              disablePictureInPicture
              playsInline
              style={{ borderRadius: "10px", display: "block" }}
            >
              <source src={videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            {autoplayBlocked && (
              <button
                type="button"
                className="video-play-overlay"
                onClick={playVideo}
                aria-label="Play video"
              >
                <span className="video-play-overlay-button">
                  <i className="fa fa-play"></i>
                </span>
                <span className="video-play-overlay-label">Play video</span>
              </button>
            )}
          </div>
        </Col>
        {!hideChapters && (
        <Col
          lg={6}
          xl={4}
          className="d-none d-lg-block"
          style={{ width: "100%" }}
        >
          <Accordion activeKey={accordionActiveKey}>
            {timeStampCategories.map((category, categoryIndex) => (
              <Card
                key={categoryIndex}
                style={{ borderBottom: "1px solid white" }}
              >
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey={`${categoryIndex}`}
                  onClick={() => handleAccordionToggle(categoryIndex)}
                  style={{
                    cursor: "pointer",
                    paddingBottom: "15px",
                    fontSize: "24px",
                    fontWeight: "400",
                    fontStyle: "italic",
                    backgroundColor:
                      activeIndex.categoryIndex === categoryIndex
                        ? "var(--blue)"
                        : "rgba(128,128,128,0.2)",
                    color:
                      activeIndex.categoryIndex === categoryIndex
                        ? "white"
                        : "black",
                  }}
                >
                  {category.categoryTitle}
                </Accordion.Toggle>
                <Accordion.Collapse eventKey={`${categoryIndex}`}>
                  <Card.Body>
                    <h4
                      style={{
                        paddingBottom: "15px",
                        fontSize: "20px",
                        fontWeight: "300",
                      }}
                    >
                      {category.categoryDescription}
                    </h4>
                    <ListGroup>
                      {category.timeStampDetails.map((detail, detailIndex) => (
                        <ListGroupItem
                          key={detailIndex}
                          onClick={() => jumpTo(detail.startTime + 1)}
                          active={
                            activeIndex.categoryIndex === categoryIndex &&
                            activeIndex.detailIndex === detailIndex
                          }
                          style={{ cursor: "pointer" }}
                        >
                          <h5>{detail.title}</h5>
                          <p
                            dangerouslySetInnerHTML={{
                              __html: formatText(detail.description),
                            }}
                          ></p>
                          {/* <p
                            style={{
                              color: "rgba(128,128,128,0.3)",
                              fontWeight: "100",
                            }}
                          >
                            Start Time: {detail.startTime}s, End Time:{" "}
                            {detail.endTime}s
                          </p> */}
                        </ListGroupItem>
                      ))}
                    </ListGroup>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            ))}
          </Accordion>
        </Col>
        )}
      </Row>
    </Container>
  );
};

export default VideoPlayer;
